import { Component, Event, EventEmitter, h, Host, JSX, Listen, Prop, State } from '@stencil/core';
import { StoryblokService, StringService, SvgType } from '../../services';
import { ButtonAppearance, ButtonFontSize, ButtonTheme, ButtonType, buttonTypeKeys, buttonTypeValues } from './rcm-button.types';

import { remCalc } from '../../style';
import { Multilink } from '../types/link.types';
import { TrackingService } from '../../services/tracking';
import { TrackingObjectBuilder } from '../../services/tracking/tracking-object-builder';

@Component({
  tag: 'rcm-button',
  styleUrl: 'rcm-button.scss',
  shadow: true,
})
export class RcmButton {
  /** Button text */
  @Prop() public text?: string;

  /** Button icon */
  @Prop() public icon: SvgType;

  /** Button icon size */
  @Prop() public iconSize: number;

  /** Button link */
  @Prop() public link?: string | Multilink;

  /** Button type */
  @Prop() public type: ButtonType = ButtonType.BUTTON;

  /** Font size */
  @Prop() public fontSize: ButtonFontSize;

  /** Is button disabled */
  @Prop() public disabled: boolean = false;

  /** Is button loading */
  @Prop() public loading: boolean = false;

  /** Button appearance */
  @Prop() public appearance: ButtonAppearance = ButtonAppearance.PRIMARY_GREY;

  @Prop() public hovered: boolean;

  @Prop() public ariaLabel: string;

  @Prop() download: boolean;

  /** Is icon on the right side of the button */
  @Prop() iconRight: boolean;

  /** Is font regular */
  @Prop() fontRegular: boolean;

  /** Button color theme */
  @Prop() theme: ButtonTheme = ButtonTheme.DARK;

  /** Button parent component */
  @Prop() parentComponent?: string;

  @State() private parsedLink?: Multilink;

  @Prop()
  expanded: boolean | undefined = undefined;

  @Prop()
  controls: string;

  @Prop()
  buttonId: string;

  @Event({ eventName: 'rcmButtonClick' })
  private buttonClickEvent: EventEmitter;

  element: HTMLAnchorElement | HTMLButtonElement;

  @Listen('focus', { capture: true })
  handleFocus() {
    this.element.focus();
  }

  private get typeParsed(): ButtonType {
    const index = buttonTypeValues.indexOf(this.type);
    return index >= 0 ? ButtonType[buttonTypeKeys[index]] : ButtonType.BUTTON;
  }

  public constructor() {
    this.handleClick = this.handleClick.bind(this);
  }

  async componentWillLoad() {
    this.parsedLink = StringService.parseLink(this.link);
  }

  onClick(event: MouseEvent) {
    if (!event.composedPath().includes(this.element)) {
      this.element.click();
    }
  }

  public render(): JSX.Element {
    const linkSrc = this.parsedLink?.src;
    const Element = linkSrc ? 'a' : 'button';
    let updatedLink = linkSrc;
    let props;
    if (linkSrc) {
      props = {
        target: this.parsedLink?.target,
        download: this.download,
      };

      if (!this.disabled) {
        updatedLink = StringService.normalizeLink(linkSrc as string);

        const href = this.download ? StoryblokService.imageSrcToDownloadLink(updatedLink) : updatedLink;
        props.href = href;
      }
    } else {
      props = {
        type: this.typeParsed,
        disabled: this.disabled || this.loading,
        appearance: this.appearance,
        download: this.download,
      };
    }

    const buttonClasses = {
      button: true,
      'button--disabled': this.disabled || this.loading,
      'button--yellow': this.appearance === ButtonAppearance.PRIMARY_YELLOW,
      'button--light-grey': this.appearance === ButtonAppearance.PRIMARY_LIGHT_GREY,
      'button--secondary-grey': this.appearance === ButtonAppearance.SECONDARY_GREY,
      'button--secondary-white': this.appearance === ButtonAppearance.SECONDARY_WHITE,
      'button--tertiary': this.appearance === ButtonAppearance.TERTIARY,
      'button--navigation-tab': this.appearance === ButtonAppearance.NAVIGATION_TAB,
      'button--navigation-tab--hovered': this.hovered,
      'button--clear': this.appearance === ButtonAppearance.CLEAR,
      'button--underline-icon': this.appearance === ButtonAppearance.UNDERLINE_ICON,
      'button--category': this.appearance === ButtonAppearance.CATEGORY,
      'button--filter': this.appearance === ButtonAppearance.FILTER,
      'button--filter light': this.appearance === ButtonAppearance.FILTER_LIGHT,
      'button--small': this.fontSize === ButtonFontSize.SMALL,
      'button--font--regular': this.fontRegular,
      'button--light': this.theme === ButtonTheme.LIGHT,
      'button--modal-close': this.appearance === ButtonAppearance.MODAL_CLOSE,
      'button--global-search': this.appearance === ButtonAppearance.GLOBAL_SEARCH,
      'button--default': this.appearance === ButtonAppearance.DEFAULT,
      'button--mobile-submenu': [ButtonAppearance.MOBILE_SUBMENU_CLOSED, ButtonAppearance.MOBILE_SUBMENU_OPEN].includes(this.appearance),
      'button--clear-input': this.appearance === ButtonAppearance.CLEAR_INPUT,
    };
    const arrowIconSize = this.fontSize === ButtonFontSize.SMALL ? 12 : 16;

    return (
      <Host onClick={this.onClick.bind(this)}>
        <Element
          {...(this.ariaLabel ? { 'aria-label': this.ariaLabel } : {})}
          class={buttonClasses}
          onClick={(e) => this.handleClick(e, props.href)}
          {...props}
          onTouchStart={(e) => {
            (e.target as Element).classList.add('button--hovered');
          }}
          ref={(el) => {
            this.element = el;
          }}
          {...(this.type === ButtonType.BUTTON && this.expanded !== undefined && { 'aria-expanded': `${this.expanded.valueOf()}` })}
          {...(this.type === ButtonType.BUTTON && this.controls && { 'aria-controls': this.controls })}
          {...(this.buttonId !== undefined && { id: this.buttonId })}
        >
          <div class={{ content: true, 'direction--reverse': this.iconRight }}>
            {this.loading && <rcm-loader size={`${remCalc(16)}rem`} noMargin />}

            {!this.loading && this.icon && (
              <rcm-icon class="icon" icon={this.icon} height={`${remCalc(this.iconSize ?? 16)}rem`} width={`${remCalc(this.iconSize ?? 16)}rem`} />
            )}

            {this.text && (
              <div class="text" data-content={this.text}>
                {this.text}
              </div>
            )}
            {this.appearance === ButtonAppearance.TERTIARY ? (
              <rcm-icon class="tertiary-icon" icon="arrow-right" height={`${remCalc(arrowIconSize)}rem`}></rcm-icon>
            ) : (
              ''
            )}
            {this.appearance === ButtonAppearance.CLEAR ? <rcm-icon class="tertiary-icon" icon="x" height={`${remCalc(16)}rem`}></rcm-icon> : ''}
            {this.appearance === ButtonAppearance.MODAL_CLOSE ? (
              <rcm-icon class="tertiary-icon" icon="x" height={`2rem`} width={'2rem'}></rcm-icon>
            ) : (
              ''
            )}
            {this.appearance === ButtonAppearance.GLOBAL_SEARCH ? (
              <rcm-icon class="tertiary-icon" icon="search" height={`${remCalc(26)}rem`} width={`${remCalc(26)}rem`}></rcm-icon>
            ) : (
              ''
            )}
            {this.appearance === ButtonAppearance.MOBILE_SUBMENU_CLOSED ? (
              <rcm-icon class="tertiary-icon" icon="chevron-down" height={`${remCalc(26)}rem`} width={`${remCalc(26)}rem`}></rcm-icon>
            ) : (
              ''
            )}
            {this.appearance === ButtonAppearance.MOBILE_SUBMENU_OPEN ? (
              <rcm-icon class="tertiary-icon" icon="chevron-up" height={`${remCalc(26)}rem`} width={`${remCalc(26)}rem`}></rcm-icon>
            ) : (
              ''
            )}
            {this.appearance === ButtonAppearance.CLEAR_INPUT ? (
              <rcm-icon class="tertiary-icon" icon="clear" height={`${remCalc(16)}rem`} width={`${remCalc(16)}rem`}></rcm-icon>
            ) : (
              ' '
            )}
          </div>
        </Element>
      </Host>
    );
  }

  private handleTracking(linkSrc: string | undefined) {
    if (!this.parentComponent) {
      return;
    }

    // BUTTON TRACKING
    if (!linkSrc) {
      TrackingService.push(
        new TrackingObjectBuilder()
          .forButtonClick({
            clickText: this.text,
            parentComponent: this.parentComponent,
          })
          .build(),
      );

      return;
    }

    // LINK TRACKING
    if (!this.download) {
      TrackingService.push(
        new TrackingObjectBuilder()
          .forLinkClick({
            clickText: this.text,
            clickUrl: linkSrc,
            parentComponent: this.parentComponent,
          })
          .build(),
      );
    }

    // DOWNLOAD TRACKING
    if (this.download) {
      const fileNameFromSrc = linkSrc.split('/').pop();
      TrackingService.push(
        new TrackingObjectBuilder()
          .forFileDownload({
            clickText: this.text,
            fileName: fileNameFromSrc,
            parentComponent: this.parentComponent,
          })
          .build(),
      );
    }
  }

  private handleClick(event: MouseEvent, linkSrc: string | undefined): void {
    event.stopPropagation();
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    this.handleTracking(linkSrc);

    this.buttonClickEvent.emit();
  }
}
